import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { resolveImageUrl, fetchCategories } from "../services/api";
import { Clock } from "lucide-react";

// Simple module-level cache for categories
let cachedCategories = null;
let fetchPromise = null;

const loadCategories = () => {
  if (cachedCategories) return Promise.resolve(cachedCategories);
  if (!fetchPromise) {
    fetchPromise = fetchCategories().then((cats) => {
      cachedCategories = cats;
      return cats;
    }).catch(() => []);
  }
  return fetchPromise;
};

const ArticleCard = ({ article, variant = "default", categories: propCategories }) => {
  const [cats, setCats] = useState(propCategories || cachedCategories || []);

  useEffect(() => {
    if (cats.length === 0) {
      loadCategories().then(setCats);
    }
  }, [cats.length]);

  const cat = cats.find((c) => c.id === article.category);
  const imgSrc = resolveImageUrl(article.image);

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-5 p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 border border-transparent hover:border-gray-100 hover:shadow-sm"
      >
        <div className="flex-shrink-0 w-48 h-32 rounded-xl overflow-hidden">
          <img
            src={imgSrc}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={{ objectPosition: `${article.image_focal_x ?? 50}% ${article.image_focal_y ?? 50}%` }}
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <span
            className="text-[11px] font-bold uppercase tracking-wider"
            style={{ color: cat?.color || "#1B7A3D" }}
          >
            {cat?.name}
          </span>
          <h3 className="text-lg font-bold text-gray-900 line-clamp-2 mt-1 group-hover:text-[#1B7A3D] transition-colors duration-200">
            {article.title}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2 mt-1.5">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <img
              src={article.author?.avatar}
              alt={article.author?.name}
              className="w-6 h-6 rounded-full"
            />
            <span className="text-xs font-medium text-gray-700">
              {article.author?.name}
            </span>
            <span className="text-gray-300">·</span>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock size={12} />
              {article.readTime}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 py-3 border-b border-gray-100 last:border-0"
      >
        <div className="flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden">
          <img
            src={imgSrc}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={{ objectPosition: `${article.image_focal_x ?? 50}% ${article.image_focal_y ?? 50}%` }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <span
            className="text-[10px] font-bold uppercase tracking-wider"
            style={{ color: cat?.color || "#1B7A3D" }}
          >
            {cat?.name}
          </span>
          <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 mt-0.5 group-hover:text-[#1B7A3D] transition-colors">
            {article.title}
          </h4>
        </div>
      </Link>
    );
  }

  // Default card
  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={imgSrc}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          style={{ objectPosition: `${article.image_focal_x ?? 50}% ${article.image_focal_y ?? 50}%` }}
        />
      </div>
      <div className="p-5">
        <span
          className="text-[11px] font-bold uppercase tracking-wider"
          style={{ color: cat?.color || "#1B7A3D" }}
        >
          {cat?.name}
        </span>
        <h3 className="text-lg font-bold text-gray-900 line-clamp-2 mt-1.5 group-hover:text-[#1B7A3D] transition-colors duration-200">
          {article.title}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-2 mt-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-50">
          <img
            src={article.author?.avatar}
            alt={article.author?.name}
            className="w-7 h-7 rounded-full"
          />
          <span className="text-xs font-medium text-gray-700">
            {article.author?.name}
          </span>
          <span className="text-gray-300">·</span>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock size={12} />
            {article.readTime}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
